/**
 * The overlay stack every Escape-answering boundary registers on.
 *
 * Module-level because overlays live in different trees: the header's disclosure panels
 * and the creatives screen's detail panel share no component above them that could hold
 * this state. Only the topmost overlay answers Escape, and closing one, whatever the
 * route, pops it off.
 *
 * Topmost is the visual stacking order, not the order things opened in. Open order was
 * the first reading and it holds for one direction only: a disclosure opened over the
 * detail panel is both later and higher, so Escape reached the right one. The other
 * direction breaks it. A disclosure is not closed by focus leaving it, so a reader can
 * open one, tab on to the table and activate a row, and the detail panel then opens
 * underneath a disclosure that is still painted above it.
 *
 * An overlay opening under an open one displaces it, which is what makes that second
 * direction a state the reader never reaches. It is not a tidiness rule: with the
 * accounts disclosure over it, every control the detail panel has is painted over, so
 * the panel has nowhere to put the focus it takes on open and no Escape of its own. A
 * press outside already dismissed the disclosure; this is the same dismissal for the
 * keyboard route that a press never reaches.
 *
 * The layers are the `z-index` ladder in `frontend/src/styles/shell.css`, which states
 * the same order for the same reason: the header and its disclosures sit above the
 * detail overlay so they stay reachable while a creative is open.
 */
export const OVERLAY_LAYER = {
  /** `.detail-panel`, z-index 71, above the backdrop and below the header band. */
  detailPanel: 71,
  /**
   * `.page-panel`, z-index 75: a disclosure anchored inside the page, the creatives filter
   * bar's column chooser and account toggles (GRO-64). Below the header band at 80, so a
   * panel on a scrolled page slides under the header rather than over it. Above the
   * detail panel, so opening a creative displaces it: a reader who tabs from an open
   * chooser into the table and opens a row gets the panel, not a chooser painted over it.
   */
  pageDisclosure: 75,
  /** `.header-panel`, z-index 81, above the header at 80 and above the detail overlay. */
  headerDisclosure: 81,
  /**
   * A modal dialog's backdrop, z-index 90, above everything else: the page under it is
   * inert while it is open, so it displaces nothing a reader could still reach (GRO-161).
   */
  dialog: 90,
} as const

interface Entry {
  id: string
  layer: number
  onDisplaced?: () => void
}

const stack: Entry[] = []

/**
 * Puts an overlay on the stack at its layer. Registering a registered id re-tops it.
 *
 * Every overlay already open above this one is displaced: it is asked to close through
 * the callback it registered with, and one that registered none is left alone. The
 * displaced overlays are read off the stack before any of them is asked, because closing
 * one unregisters it and the list would move underneath the walk.
 */
export function registerOverlay(id: string, layer: number, onDisplaced?: () => void): void {
  const at = stack.findIndex((entry) => entry.id === id)
  if (at !== -1) stack.splice(at, 1)
  const displaced = stack.filter((entry) => entry.layer > layer)
  stack.push({ id, layer, onDisplaced })
  for (const entry of displaced) entry.onDisplaced?.()
}

export function unregisterOverlay(id: string): void {
  const at = stack.findIndex((entry) => entry.id === id)
  if (at !== -1) stack.splice(at, 1)
}

/**
 * Only the stack's top answers Escape; anything beneath it is not the boundary in play.
 * The top is the highest layer, and the last registered among equals - two disclosures
 * share a layer, and there the one opened second is the one on top.
 */
export function isTopmostOverlay(id: string): boolean {
  if (stack.length === 0) return false
  let top = stack[0]
  for (const entry of stack) {
    if (entry.layer >= top.layer) top = entry
  }
  return top.id === id
}
